import React, { useMemo, useState } from "react";
import { listApplications, listJobs } from "./aiHireApi";
import { AI_SHORTLIST_THRESHOLD, TAB_STAGES, stageOf } from "./aiHireStages";
import CandidateDrawer from "./CandidateDrawer";
import {
  AiScoreBadge,
  ChipGroup,
  EmptyRow,
  FilterSelect,
  StageChip,
  Toolbar,
  fmtDate,
  useAiHireData,
} from "./AiHireUi";

const BOARD_STAGES = ["applied", "ai_screened", "ai_interviewed", "shortlisted"];
const VIEWS = [
  { id: "board", label: "Board" },
  { id: "list", label: "List" },
];

/** Best score so far: AI interview when taken, else resume screening. */
function scoreOf(app) {
  if (app.aiInterview) return { score: app.aiInterview.score, recommendation: app.aiInterview.recommendation };
  return { score: app.aiScreenScore, recommendation: null };
}

function CandidateCard({ app, onOpen }) {
  const { score, recommendation } = scoreOf(app);
  const suggest = app.stage === "ai_interviewed" && score >= AI_SHORTLIST_THRESHOLD;
  return (
    <button type="button" className="aihire-card" onClick={() => onOpen(app)}>
      <span className="aihire-card__top">
        <b>{app.candidate.name}</b>
        <AiScoreBadge score={score} recommendation={recommendation} />
      </span>
      <span className="aihire-card__meta">
        {app.job.title} · {app.candidate.location}
      </span>
      <span className="aihire-card__meta">
        {app.candidate.experienceYears} yrs · notice {app.candidate.noticeDays || 0} d
      </span>
      {suggest ? <span className="aihire-card__hint">Ai Hire suggests shortlisting</span> : null}
      {app.stage === "shortlisted" ? <span className="aihire-card__hint">Waiting for an interview slot</span> : null}
    </button>
  );
}

/** Candidate Profiles: Applied through Shortlisted, with Ai Hire screening and interview scores. */
function CandidateProfilesTab({ jobId, onJobChange }) {
  const apps = useAiHireData(() => listApplications({ stages: TAB_STAGES.candidates }), []);
  const jobs = useAiHireData(listJobs, []);
  const [stage, setStage] = useState("");
  const [location, setLocation] = useState("");
  const [view, setView] = useState("board");
  const [open, setOpen] = useState(null);

  const scoped = useMemo(
    () => (apps || []).filter((a) => (!jobId || a.jobId === jobId) && (!location || a.candidate.location === location)),
    [apps, jobId, location]
  );
  const visible = stage ? scoped.filter((a) => a.stage === stage) : scoped;
  const locations = useMemo(() => [...new Set((apps || []).map((a) => a.candidate.location))].sort(), [apps]);

  const stageOptions = [
    { id: "", label: "All", count: scoped.length },
    ...TAB_STAGES.candidates.map((id) => ({
      id,
      label: stageOf(id).label,
      count: scoped.filter((a) => a.stage === id).length,
    })),
  ];
  const columns = stage ? [stage] : BOARD_STAGES;

  return (
    <div className="aihire-tab">
      <Toolbar>
        <ChipGroup label="Stage" options={stageOptions} value={stage} onChange={setStage} />
      </Toolbar>
      <Toolbar>
        <FilterSelect
          label="Job"
          value={jobId || ""}
          onChange={onJobChange}
          options={(jobs || []).map((j) => ({ value: j.id, label: `${j.title} (${j.client})` }))}
          allLabel="All jobs"
        />
        <FilterSelect label="Candidate location" value={location} onChange={setLocation} options={locations} />
        <span className="aihire-toolbar__spacer" />
        <ChipGroup label="View" options={VIEWS} value={view} onChange={setView} />
      </Toolbar>

      {view === "board" ? (
        <div className="aihire-board" style={{ "--aihire-cols": columns.length }}>
          {columns.map((id) => {
            const list = visible.filter((a) => a.stage === id);
            return (
              <section key={id} className="aihire-board__col">
                <header>
                  <StageChip stage={id} />
                  <em>{list.length}</em>
                </header>
                {list.length === 0 ? <p className="aihire-muted">Nobody here</p> : null}
                {list.map((a) => (
                  <CandidateCard key={a.id} app={a} onOpen={setOpen} />
                ))}
              </section>
            );
          })}
        </div>
      ) : (
        <div className="aihire-table-wrap">
          <table className="aihire-table">
            <thead>
              <tr>
                <th>Candidate</th>
                <th>Job</th>
                <th>Client / Project</th>
                <th>Location</th>
                <th className="num">Exp</th>
                <th className="num">Notice</th>
                <th>AI screen</th>
                <th>AI interview</th>
                <th>Stage</th>
                <th>Applied</th>
              </tr>
            </thead>
            <tbody>
              {visible.length === 0 ? <EmptyRow colSpan={10}>No candidates match these filters.</EmptyRow> : null}
              {visible.map((a) => (
                <tr key={a.id} className="is-clickable" onClick={() => setOpen(a)}>
                  <td>
                    <button type="button" className="aihire-link">
                      {a.candidate.name}
                    </button>
                  </td>
                  <td>{a.job.title}</td>
                  <td>
                    {a.job.client} / {a.job.project}
                  </td>
                  <td>{a.candidate.location}</td>
                  <td className="num">{a.candidate.experienceYears} y</td>
                  <td className="num">{a.candidate.noticeDays} d</td>
                  <td>
                    <AiScoreBadge score={a.aiScreenScore} />
                  </td>
                  <td>
                    <AiScoreBadge score={a.aiInterview?.score} recommendation={a.aiInterview?.recommendation} />
                  </td>
                  <td>
                    <StageChip stage={a.stage} />
                  </td>
                  <td>{fmtDate(a.appliedOn)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open ? <CandidateDrawer app={open} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}

export default CandidateProfilesTab;
